import React, { useState, useEffect, useCallback } from 'react'
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Database, 
  Server, 
  TrendingUp, 
  Users, 
  Zap,
  AlertCircle,
  Timer,
  BarChart3,
  RefreshCw,
  Shield,
  Cloud,
  Cpu,
  HardDrive
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { AdminLayout } from '../components/admin/AdminLayout'
import { 
  DashboardService, 
  type HealthOverview,
  type FailureCategory,
  type ExecutionItem,
  type Alert,
  type ServiceHealth,
  type DashboardData
} from '../services/dashboardService'

// Default empty state for initial load
const defaultHealthData: HealthOverview = {
  healthScore: 0,
  systemStatus: 'EXCELLENT',
  totalExecutions: 0,
  successRate: 100,
  averageProcessingTime: 0,
  activeExecutions: 0,
  completedToday: 0,
  failedToday: 0,
  errorSources: {
    d1Database: 0,
    aiService: 0,
    r2Storage: 0,
    pdfGenerator: 0,
    other: 0
  },
  criticalIssues: 0,
  serviceDowntime: 0
}


export function AdminDashboardPage() {
  // State management
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lastRefresh, setLastRefresh] = useState(new Date())
  
  // Dashboard data state
  const [healthData, setHealthData] = useState<HealthOverview>(defaultHealthData)
  const [failures, setFailures] = useState<FailureCategory[]>([])
  const [executions, setExecutions] = useState<ExecutionItem[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [services, setServices] = useState<ServiceHealth[]>([])
  
  // Auto-refresh interval (15 seconds)
  const REFRESH_INTERVAL = 15000

  // Load dashboard data
  const loadDashboardData = useCallback(async () => {
    try {
      setError(null)
      const data = await DashboardService.getAllDashboardData()
      
      if (data.health) {
        setHealthData(data.health)
      }
      setFailures(data.failures)
      setExecutions(data.executions)
      setServices(data.services)
      setAlerts(data.alerts)
      setLastRefresh(data.lastUpdated)
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
      setError('Failed to load dashboard data. Please try again.')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  // Manual refresh handler
  const handleRefresh = async () => {
    setRefreshing(true)
    await loadDashboardData()
  }
  
  // Initial load and auto-refresh setup
  useEffect(() => {
    loadDashboardData()
    
    // Set up auto-refresh
    const intervalId = setInterval(() => {
      loadDashboardData()
    }, REFRESH_INTERVAL)
    
    // Cleanup on unmount
    return () => clearInterval(intervalId)
  }, [loadDashboardData])

  const getHealthColor = (status: string) => {
    switch (status) {
      case 'EXCELLENT': return 'from-green-500 to-emerald-500'
      case 'GOOD': return 'from-blue-500 to-cyan-500'
      case 'FAIR': return 'from-yellow-500 to-orange-500'
      case 'POOR': return 'from-red-500 to-pink-500'
      default: return 'from-gray-500 to-slate-500'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'processing': return <Activity className="h-4 w-4 animate-pulse text-blue-600" />
      case 'queued': return <Clock className="h-4 w-4 text-orange-600" />
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-600" />
      case 'failed': return <AlertCircle className="h-4 w-4 text-red-600" />
      default: return <Clock className="h-4 w-4 text-gray-600" />
    }
  }

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'processing': return 'secondary'
      case 'queued': return 'outline'
      case 'completed': return 'default'
      case 'failed': return 'destructive'
      default: return 'outline'
    }
  }

  const getAlertIcon = (priority: string) => {
    switch (priority) {
      case 'HIGH': return <AlertTriangle className="h-4 w-4 text-red-600" />
      case 'MEDIUM': return <AlertCircle className="h-4 w-4 text-orange-600" />
      case 'LOW': return <AlertCircle className="h-4 w-4 text-yellow-600" />
      default: return <AlertCircle className="h-4 w-4 text-gray-600" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'HIGH': return 'border-red-300 bg-red-50'
      case 'MEDIUM': return 'border-orange-300 bg-orange-50'
      case 'LOW': return 'border-yellow-300 bg-yellow-50'
      default: return 'border-gray-300 bg-gray-50'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'AI_SERVICE': return 'border-red-300 bg-red-50 text-red-900'
      case 'DATABASE_CONSTRAINT': return 'border-orange-300 bg-orange-50 text-orange-900'
      case 'PDF_GENERATION': return 'border-yellow-300 bg-yellow-50 text-yellow-900'
      case 'TIMEOUT': return 'border-blue-300 bg-blue-50 text-blue-900'
      case 'R2_CONNECTION': return 'border-green-300 bg-green-50 text-green-900'
      default: return 'border-gray-300 bg-gray-50 text-gray-900'
    }
  }

  const getServiceIcon = (name: string) => {
    switch (name.toLowerCase()) {
      case 'ai orchestrator': return <Cpu className="h-4 w-4" />
      case 'd1 database': return <Database className="h-4 w-4" />
      case 'r2 storage': return <HardDrive className="h-4 w-4" />
      case 'pdf generator': return <Server className="h-4 w-4" />
      default: return <Cloud className="h-4 w-4" />
    }
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  const formatTime = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    })
  }

  // Loading state
  if (loading && !refreshing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard data...</p>
        </div>
      </div>
    )
  }

  return (
    <AdminLayout 
      title="Admin Dashboard"
      subtitle="System monitoring and analytics"
    >
      {/* Refresh Controls */}
      <div className="flex justify-end mb-6">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Last updated: {lastRefresh.toLocaleTimeString()}
            <span className="text-xs text-gray-400 ml-2">(Auto-refresh: 15s)</span>
          </div>
          <Button 
            onClick={handleRefresh}
            disabled={refreshing}
            size="sm"
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

        {/* System Health Score Header */}
        <Card className="mb-8 overflow-hidden">
          <div className={`bg-gradient-to-r ${getHealthColor(healthData.systemStatus)} p-6 text-white`}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">System Health Score</h2>
                <div className="flex items-center gap-6">
                  <div className="text-4xl font-bold">{healthData.healthScore}</div>
                  <div>
                    <div className="text-lg font-semibold">{healthData.systemStatus}</div>
                    <div className="text-sm opacity-90">
                      {healthData.criticalIssues > 0 
                        ? `⚠️ ${healthData.criticalIssues} critical issues require attention`
                        : 'All systems operational'
                      }
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm opacity-90 mb-1">Success Rate</div>
                <div className={`text-2xl font-bold ${
                  healthData.successRate < 95 ? 'text-red-200' : 'text-white'
                }`}>{healthData.successRate}%</div>
                <div className="text-sm opacity-90 mt-2">
                  Avg Time: {healthData.averageProcessingTime}s
                  {healthData.averageProcessingTime > 60 && ' ⚠️'}
                </div>
                {healthData.serviceDowntime > 0 && (
                  <div className="text-sm bg-red-500/30 px-2 py-1 rounded mt-1">
                    {healthData.serviceDowntime}m downtime today
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Error Sources Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <Card className="border-red-200">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{healthData.errorSources.aiService}</div>
                <div className="text-sm text-gray-600">AI Service</div>
                <div className="text-xs text-red-600 font-medium">Critical</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{healthData.errorSources.d1Database}</div>
                <div className="text-sm text-gray-600">Database</div>
                <div className="text-xs text-orange-600 font-medium">High</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{healthData.errorSources.pdfGenerator}</div>
                <div className="text-sm text-gray-600">PDF Gen</div>
                <div className="text-xs text-yellow-600 font-medium">Medium</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{healthData.errorSources.r2Storage}</div>
                <div className="text-sm text-gray-600">R2 Storage</div>
                <div className="text-xs text-blue-600 font-medium">Low</div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-200">
            <CardContent className="p-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{healthData.errorSources.other}</div>
                <div className="text-sm text-gray-600">Other</div>
                <div className="text-xs text-gray-600 font-medium">Mixed</div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Key Performance Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Active Jobs</p>
                  <p className={`text-3xl font-bold ${
                    healthData.activeExecutions > 5 ? 'text-orange-600' : 'text-blue-600'
                  }`}>{healthData.activeExecutions}</p>
                  {healthData.activeExecutions > 5 && (
                    <p className="text-xs text-orange-600 font-medium">High load</p>
                  )}
                </div>
                <Activity className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Total Executions</p>
                  <p className="text-3xl font-bold text-gray-900">{healthData.totalExecutions}</p>
                  <p className="text-xs text-gray-500 mt-1">All time</p>
                </div>
                <BarChart3 className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Completed Today</p>
                  <p className="text-3xl font-bold text-green-600">{healthData.completedToday}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Failed Today</p>
                  <p className={`text-3xl font-bold ${
                    healthData.failedToday > 5 ? 'text-red-600' : 'text-orange-600'
                  }`}>{healthData.failedToday}</p>
                  {healthData.failedToday > 5 && (
                    <p className="text-xs text-red-600 font-medium">⚠️ Above threshold</p>
                  )}
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Live Monitoring Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* Live Executions */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-blue-600" />
                  Live Execution Monitor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {executions.map((execution) => (
                    <div 
                      key={execution.trackingId} 
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(execution.status)}
                        <div>
                          <div className="font-medium">{execution.companyName}</div>
                          <div className="text-sm text-gray-600">
                            {execution.trackingId} • {execution.industry}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant={getStatusVariant(execution.status)} className="mb-1">
                          {execution.status}
                        </Badge>
                        <div className="text-xs text-gray-500">
                          {execution.status === 'processing' || execution.status === 'completed' 
                            ? formatDuration(execution.duration)
                            : execution.status === 'queued' 
                            ? formatTime(execution.startTime)
                            : 'Failed'
                          }
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Critical Alerts Panel */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Critical System Alerts ({alerts.filter(a => a.priority === 'HIGH').length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {alerts.map((alert) => (
                  <div key={alert.id} className={`p-3 border rounded-lg ${getPriorityColor(alert.priority)}`}>
                    <div className="flex items-start gap-2 mb-2">
                      {getAlertIcon(alert.priority)}
                      <div className="flex-1">
                        <div className="font-semibold text-sm flex items-center gap-2">
                          {alert.title}
                          <Badge variant={alert.priority === 'HIGH' ? 'destructive' : alert.priority === 'MEDIUM' ? 'secondary' : 'outline'} className="text-xs">
                            {alert.priority}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-700 mt-1">{alert.description}</div>
                        {alert.errorCode && (
                          <div className="text-xs font-mono bg-gray-100 px-2 py-1 rounded mt-1">
                            {alert.errorCode}
                          </div>
                        )}
                        <div className="text-xs font-medium text-blue-700 mt-2">
                          💡 {alert.recommendedAction}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600">
                      <span className="flex items-center gap-2">
                        {alert.service} • {alert.affectedCount} affected
                        {alert.autoResolvable && <span className="text-green-600">🔄 Auto-resolvable</span>}
                      </span>
                      <span>{alert.timestamp}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Analytics Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Detailed Failure Analysis */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                Failure Pattern Analysis (Last 24h)
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-96 overflow-y-auto">
              <div className="space-y-4">
                {failures.map((failure, index) => (
                  <div key={index} className={`p-4 border rounded-lg ${getCategoryColor(failure.category)}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="font-bold text-sm">{failure.category.replace('_', ' ')}</div>
                          <Badge variant={failure.priority === 'HIGH' ? 'destructive' : failure.priority === 'MEDIUM' ? 'secondary' : 'outline'} className="text-xs">
                            {failure.priority}
                          </Badge>
                          <div className={`text-xs px-2 py-1 rounded ${
                            failure.trend === 'up' ? 'bg-red-100 text-red-700' :
                            failure.trend === 'down' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {failure.trend === 'up' ? '↗ Increasing' : failure.trend === 'down' ? '↘ Decreasing' : '→ Stable'}
                          </div>
                        </div>
                        <div className="text-xs text-gray-700 mb-2">
                          <strong>{failure.count} failures</strong> ({failure.percentage}%) • Last: {failure.latestOccurrence}
                        </div>
                      </div>
                    </div>
                    
                    {/* Error Message */}
                    <div className="bg-gray-100 p-2 rounded text-xs font-mono mb-2 border-l-4 border-red-400">
                      🚨 {failure.sampleErrorMessage}
                    </div>
                    
                    {/* Recommendation */}
                    <div className="bg-blue-50 p-2 rounded text-xs mb-2 border-l-4 border-blue-400">
                      💡 <strong>Recommended Fix:</strong> {failure.troubleshootingRecommendation}
                    </div>
                    
                    {/* Affected Services */}
                    <div className="flex items-center gap-1 mt-2">
                      <span className="text-xs text-gray-600">Affected:</span>
                      {failure.affectedServices.map((service, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{service}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Detailed Service Health Matrix */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-600" />
                Service Health Matrix
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={index} className={`p-4 border rounded-lg ${
                    service.status === 'healthy' ? 'border-green-200 bg-green-50' :
                    service.status === 'degraded' ? 'border-orange-200 bg-orange-50' :
                    service.status === 'down' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {getServiceIcon(service.name)}
                        <div>
                          <div className="font-bold text-sm">{service.name}</div>
                          <div className="flex items-center gap-4 text-xs text-gray-700">
                            <span>{service.uptime}% uptime</span>
                            <span>{service.responseTime}ms avg</span>
                            <span className={service.errorRate > 5 ? 'text-red-600 font-medium' : 'text-gray-600'}>
                              {service.errorRate}% error rate
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge 
                          variant={
                            service.status === 'healthy' ? 'default' :
                            service.status === 'degraded' ? 'secondary' : 'destructive'
                          }
                          className="mb-1"
                        >
                          {service.status}
                        </Badge>
                        <div className="text-xs text-gray-600">
                          {service.errorCount24h} errors/24h
                        </div>
                      </div>
                    </div>
                    
                    {/* Capacity and Throughput */}
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Throughput</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${service.throughput}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{service.throughput}%</span>
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-600 mb-1">Capacity</div>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div className={`h-2 rounded-full ${
                              service.capacity > 80 ? 'bg-red-500' : service.capacity > 60 ? 'bg-orange-500' : 'bg-green-500'
                            }`} style={{ width: `${service.capacity}%` }}></div>
                          </div>
                          <span className="text-xs font-medium">{service.capacity}%</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Last Error */}
                    {service.lastError && (
                      <div className="bg-red-100 p-2 rounded text-xs font-mono border-l-4 border-red-400">
                        🚨 Latest Error: {service.lastError}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
    </AdminLayout>
  )
}